import { Component } from "react";
import BaseModal, { ModalActions, SaveButton, CancelButton } from "../../../../containers/UI/Modal/BaseModal";
import InputRow from "../../../configuration/Rows/InputRow/InputRow";
import SelectRow from "../../../configuration/Rows/SelectRow/SelectRow";
import axios from "../../../../axios";
import classes from "./DeviceModal.css";

class DeviceModal extends Component {

  state = {
    loading: true,
    deviceDetails: null,
    customersOptions: [],
    organizationsOptions: [],
    valuesChanged: false,
    errorMessage: null,
    catalogMetadata: [],
    device: {
      serial: "",
      appEUI: "",
      devEUI: "",
      appKey: "",
      deviceUuid: "",
      customerId: null,
      organizationId: null,
      manufacturer: "",
      model: "",
      modelID: "",
      softwareVersion: "",
      productionDate: ""
    }
  }

  componentDidMount() {
    this.loadCustomers();
    this.loadOrganizations();
    this.loadCatalogMetadata();
    if (this.props.selectedDevice) {
      this.loadDeviceDetails();
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedDevice !== this.props.selectedDevice) {
      if (this.props.selectedDevice) {
        this.loadDeviceDetails();
      } else {
        this.setState({
          loading: false,
          deviceDetails: null,
          errorMessage: null,
          device: {
            serial: "",
            appEUI: "",
            devEUI: "",
            appKey: "",
            deviceUuid: "",
            customerId: null,
            organizationId: null,
            manufacturer: "",
            model: "",
            modelID: "",
            softwareVersion: "",
            productionDate: ""
          }
        });
      }
    }
  }

  loadCustomers = () => {
    axios.get("/customers/allReduced")
      .then(response => {
        if (response.data) {
          const options = response.data.map(customer => ({
            label: customer.name,
            value: customer._id
          }));
          this.setState({ customersOptions: options });
        }
      })
      .catch(err => {
        console.error("Failed to load customers:", err);
      });
  }

  loadOrganizations = () => {
    axios.get("/platforms/companies")
      .then(response => {
        if (response.data && response.data.companies) {
          const options = response.data.companies.map(org => ({
            label: org.companyName,
            value: org._id
          }));
          this.setState({ organizationsOptions: options });
        }
      })
      .catch(err => {
        console.error("Failed to load organizations:", err);
      });
  }

  loadDeviceDetails = () => {
    const { selectedDevice } = this.props;
    if (!selectedDevice) return;

    this.setState({ loading: true });

    axios.get(`/platforms/devices/${selectedDevice._id}`)
      .then(response => {
        if (response.data && response.data.device) {
          const device = response.data.device;

          const vendorOptions = this.state.catalogMetadata?.vendorOptions
          const modelOptionsByVendor = this.state.catalogMetadata?.modelOptions

          const vendorKey = Array.isArray(vendorOptions)
            ? (vendorOptions.find(o => o.label === device.manufacturer) ?? {}).value
            : ""

          const modelID = vendorKey && modelOptionsByVendor && typeof modelOptionsByVendor === "object"
            ? ((modelOptionsByVendor[vendorKey] || []).find(o => o.label === device.model) ?? {}).value
            : ""

          this.setState({
            deviceDetails: device,
            device: {
              serial: device.serial || "",
              appEUI: device.appEUI || "",
              devEUI: device.devEUI || "",
              appKey: device.appKey || "",
              deviceUuid: device.deviceUuid || "",
              customerId: device.customer ? device.customer._id : null,
              organizationId: device.organization ? device.organization._id : null,
              manufacturer: vendorKey || "",
              model: device.model || "",
              modelID: modelID || "",
              softwareVersion: device.softwareVersion || "",
              productionDate: device.productionDate ? new Date(device.productionDate * 1000).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }) : ""
            },
            loading: false
          });
        }
      })
      .catch(err => {
        console.error("Failed to load device details:", err);
        this.setState({ loading: false });
      });
  }

  updateProperty = (property, value) => {
    this.setState(prev => ({
      device: {
        ...prev.device,
        [property]: value
      },
      valuesChanged: true,
      errorMessage: null
    }));
  }

  isFormValid = () => {
    const { device } = this.state;
    // Check if all required fields are filled
    return device.appEUI && device.appEUI.trim() !== '' &&
      device.devEUI && device.devEUI.trim() !== '' &&
      device.appKey && device.appKey.trim() !== '';
  }

  saveHandler = () => {
    const { device, deviceDetails } = this.state;
    const { selectedDevice } = this.props;

    // Convert production date to timestamp if provided
    let productionDate = null;
    if (device.productionDate) {
      const dateParts = device.productionDate.split('/');
      if (dateParts.length === 3) {
        const date = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
        productionDate = Math.round(date.getTime() / 1000);
      }
    }

    const deviceData = {
      serial: device.serial || null,
      appEUI: device.appEUI,
      devEUI: device.devEUI,
      appKey: device.appKey,
      modelID: device.modelID || null,
      customerId: device.customerId || null,
      organizationId: device.organizationId || null,
      softwareVersion: device.softwareVersion || null,
      productionDate: productionDate || null
    };

    this.setState({ loading: true, errorMessage: null });

    if (selectedDevice && deviceDetails) {
      // Update existing device
      axios.put(`/platforms/devices/${selectedDevice._id}`, deviceData)
        .then(() => {
          this.props.closeModal();
        })
        .catch(err => {
          let message = "Failed to update device.";
          if (err.response?.data?.message) {
            message = err.response.data.message;
          }
          this.setState({ loading: false, errorMessage: message });
        });
    } else {
      // Create new device
      axios.post("/platforms/devices", deviceData)
        .then(() => {
          this.props.closeModal();
        })
        .catch(err => {
          let message = "Failed to create device.";
          if (err.response?.data?.message) {
            message = err.response.data.message;
          }
          this.setState({ loading: false, errorMessage: message });
        });
    }
  }

  loadCatalogMetadata = async () => {
    try {
      const response = await axios.get("/platforms/devices/catalog/metadata")
      if (response.data) {
        this.setState({ catalogMetadata: {
          vendorOptions: response.data.vendorOptions,
          modelOptions: response.data.modelOptions
        } }, () => {
          const { deviceDetails, catalogMetadata } = this.state
          if (!deviceDetails) return

          const vendorOptions = catalogMetadata?.vendorOptions
          const modelOptionsByVendor = catalogMetadata?.modelOptions
          if (!Array.isArray(vendorOptions) || !modelOptionsByVendor || typeof modelOptionsByVendor !== "object") return

          const vendorKey = (vendorOptions.find(o => o.label === deviceDetails.manufacturer) ?? {}).value
          const modelID = vendorKey
            ? ((modelOptionsByVendor[vendorKey] || []).find(o => o.label === deviceDetails.model) ?? {}).value
            : ""

          this.setState(prev => ({
            device: {
              ...prev.device,
              manufacturer: vendorKey || prev.device.manufacturer || "",
              modelID: modelID || prev.device.modelID || ""
            }
          }))
        });
      }
    } catch (err) {
      console.error("Failed to load catalog metadata:", err);
      this.setState({ catalogMetadata: [] });
    }
  }

  render() {
    const { device, loading, deviceDetails, customersOptions, organizationsOptions, valuesChanged, catalogMetadata } = this.state;
    const { selectedDevice } = this.props;

    if (loading && selectedDevice) {
      return (
        <BaseModal
          isOpen={this.props.shouldShow}
          onClose={this.props.closeModal}
          title="Device Details"
        >
          <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
        </BaseModal>
      );
    }

    const createMode = !selectedDevice;

    return (
      <BaseModal
        isOpen={this.props.shouldShow}
        onClose={this.props.closeModal}
        title={createMode ? "New Device" : "Device Details"}
      >
        <InputRow
          title="AppEUI *"
          firstRow
          ultrawide
          enabled
          value={device.appEUI}
          placeholder="16 character hex string (e.g. 70B3D52DD6000000)"
          onChange={(event) => this.updateProperty("appEUI", event.target.value)}
        />
        <InputRow
          title="DevEUI *"
          ultrawide
          enabled
          value={device.devEUI}
          placeholder="16 character hex string (e.g. 70B3D52DD6000627)"
          onChange={(event) => this.updateProperty("devEUI", event.target.value)}
        />
        <InputRow
          title="AppKey *"
          ultrawide
          enabled
          value={device.appKey}
          placeholder="32 character hex string"
          onChange={(event) => this.updateProperty("appKey", event.target.value)}
        />
        <InputRow
          title="Serial Number"
          ultrawide
          enabled
          value={device.serial}
          placeholder="e.g. B37058D500100000"
          onChange={(event) => this.updateProperty("serial", event.target.value)}
        />
        {!createMode && (
          <InputRow
            title="Unique code (UUID)"
            ultrawide
            value={device.deviceUuid}
            placeholder="—"
          />
        )}

        <SelectRow
          title="Sold to"
          ultrawide
          increasedPadding
          value={device.customerId || ""}
          onChange={(newValue) => this.updateProperty("customerId", newValue ? newValue.value : null)}
          options={customersOptions}
        />
        <SelectRow
          title="Claimed by"
          ultrawide
          increasedPadding
          value={device.organizationId || ""}
          onChange={(newValue) => this.updateProperty("organizationId", newValue ? newValue.value : null)}
          options={organizationsOptions}
        />

        <SelectRow
          title="Manufacturer"
          ultrawide
          increasedPadding
          isMulti={false}
          options={catalogMetadata.vendorOptions || []}
          value={device.manufacturer || ""}
          onChange={(newValue) => {
            const selected = Array.isArray(newValue) ? newValue[0] : newValue
            this.updateProperty("manufacturer", selected ? selected.value : "")
            this.updateProperty("modelID", "")
          }}
        />
        <SelectRow
          title="Model"
          ultrawide
          increasedPadding
          isMulti={false}
          options={
            (catalogMetadata.modelOptions && device.manufacturer && catalogMetadata.modelOptions[device.manufacturer]) || []
          }
          value={device.modelID || ""}
          onChange={(newValue) => {
            const selected = Array.isArray(newValue) ? newValue[0] : newValue
            this.updateProperty("modelID", selected ? selected.value : "")
          }}
        />
        <InputRow
          title="Software version"
          ultrawide
          enabled
          value={device.softwareVersion}
          placeholder="e.g. 1.0.0"
          onChange={(event) => this.updateProperty("softwareVersion", event.target.value)}
        />
        <InputRow
          title="Production date"
          ultrawide
          enabled
          value={device.productionDate}
          placeholder="DD/MM/YYYY"
          onChange={(event) => this.updateProperty("productionDate", event.target.value)}
        />

        {this.state.errorMessage && (
          <div className={classes.errorMessage}>
            {this.state.errorMessage}
          </div>
        )}

        <ModalActions>
          <CancelButton onClick={this.props.closeModal}>Close</CancelButton>
          <SaveButton
            onClick={this.saveHandler}
            disabled={loading || (createMode && !this.isFormValid()) || (!createMode && !valuesChanged) || !device.modelID}
          >
            {createMode ? "Create" : "Save"}
          </SaveButton>
        </ModalActions>
      </BaseModal>
    );
  }
}

export default DeviceModal;

